<script setup lang="ts">
import { inject, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useOpsStore } from '@/stores/ops'
import { useAssignmentsStore } from '@/stores/assignments'
import { useTeamsStore } from '@/stores/teams'
import { useFormsStore } from '@/stores/forms'
import { TeamType } from '@/types'
import type { Timestamp } from 'firebase/firestore'

const opsStore = useOpsStore()
const assignmentsStore = useAssignmentsStore()
const teamsStore = useTeamsStore()
const formsStore = useFormsStore()
const router = useRouter()
const incidentId = inject<string>('incidentId', '')

watch(
  () => opsStore.selectedOpId,
  (opId) => {
    if (incidentId && opId) assignmentsStore.subscribeToAssignments(incidentId, opId)
  },
  { immediate: true },
)

const k9Items = computed(() =>
  assignmentsStore.teamAssignments
    .map((ta) => {
      const assignment = assignmentsStore.assignments.find((a) => a.assignment_id === ta.assignment_id)
      const team = teamsStore.teams.find((t) => t.team_id === ta.team_id)
      return { ta, assignment, team }
    })
    .filter((item) => item.assignment && item.team && item.team.team_type === TeamType.K9),
)

function lastEditedText(taId: string): string {
  const form = formsStore.forms[`SAR111_${taId}`]
  if (!form?.last_updated_at) return 'Not started'
  const ts = form.last_updated_at as Timestamp
  const date = ts.toDate?.()
  return date ? date.toLocaleString() : 'Not started'
}

function navigate(taId: string) {
  router.push({ name: 'incident-form-sar111', params: { id: incidentId, taId } })
}
</script>

<template>
  <div class="h-full grid" style="grid-template-rows: 48px 1fr; min-height: 0;">
    <!-- Sub-toolbar -->
    <div class="flex items-center gap-2.5 px-4 bg-surface border-b border-border shrink-0">
      <button
        class="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.10em] uppercase text-outline hover:text-primary transition-colors bg-transparent border-none cursor-pointer p-0"
        @click="router.push({ name: 'incident-forms', params: { id: incidentId } })"
      >
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        Forms
      </button>
      <span class="w-px h-[18px] bg-border shrink-0" />
      <span class="font-headline font-black text-[13px] uppercase tracking-[0.04em] text-on-surface">SAR 111 · K9 Team Debriefs</span>
    </div>

    <!-- Content -->
    <div class="overflow-y-auto min-h-0">
      <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
        <!-- Section head -->
        <div class="flex flex-col gap-0.5">
          <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">SAR 111 · K9 Team Debrief</span>
          <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">K9 Team Debriefs</h2>
          <p class="text-[13px] text-outline m-0">One form per K9 team assignment in the current operational period.</p>
        </div>

        <!-- Empty state -->
        <div
          v-if="k9Items.length === 0"
          class="bg-surface border border-border rounded p-10 text-center"
        >
          <p class="font-mono text-[12px] text-outline">No K9 team assignments in this operational period yet.</p>
        </div>

        <!-- K9 debrief cards -->
        <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <button
            v-for="{ ta, assignment, team } in k9Items"
            :key="ta.ta_id"
            type="button"
            class="text-left bg-surface border border-border rounded flex flex-col hover:bg-surface-container-low transition-colors focus:outline-none focus:ring-1 focus:ring-primary/30"
            @click="navigate(ta.ta_id)"
          >
            <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
              <span class="font-mono font-semibold text-[11px] tracking-[0.06em] text-on-surface">{{ team!.team_identifier }}</span>
              <span class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low ml-auto">SAR 111</span>
            </div>
            <div class="px-3.5 py-2.5 flex flex-col gap-1">
              <p class="font-body text-[12px] text-outline leading-snug">{{ assignment!.assignment_name }}</p>
              <p
                class="font-mono text-[10px] tracking-[0.06em] uppercase"
                :class="formsStore.forms[`SAR111_${ta.ta_id}`]?.last_updated_at ? 'text-primary' : 'text-outline'"
              >
                {{ lastEditedText(ta.ta_id) }}
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
